import { jsx as _jsx } from "react/jsx-runtime";
import { throttleIfPossible } from "@faire/web--source/common/globals/throttleIfPossible";
import { getOverride } from "@faire/web--source/common/PublicSettings";
import { BATCH_SETTING_THROTTLE_MS, sharedAssignSetting, unthrottledBatchAssignSetting } from "@faire/web--source/common/sharedAssignSetting";
import { sharedGetSetting } from "@faire/web--source/common/sharedGetSetting";
import { useRefFrom } from "@faire/web--source/ui/hooks/useRefFrom";
import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
// Create the context at the top level to ensure that the same context is used across the application.
var TopLevelSettingsContext = /*#__PURE__*/ createContext(undefined);
/**
 * Creates a new typesafe provider and hooks for the settings context associated with a specific Realm, i.e. Brand, Retailer, Admin, etc.
 */ export var createSettingsContextProvider = function() {
    var useSetting = function useSetting(settingOrKey, manualDefaultValue) {
        var context = useContext(SettingsContext);
        if (!context) {
            throw new Error("useSetting must be used within a SettingsContextProvider");
        }
        var settings = context.settings;
        var settingKey = typeof settingOrKey === "string" ? settingOrKey : settingOrKey.name;
        // We read this outside of the memo, to ensure any overrides are picked up
        // properly in unit tests.
        var overriddenValue = getOverride(settingKey);
        return useMemo(function() {
            return sharedGetSetting(settingOrKey, settings, overriddenValue, manualDefaultValue);
        }, [
            settingOrKey,
            settings,
            overriddenValue,
            manualDefaultValue
        ]);
    };
    var useAssignSetting = /**
     * Assigns a given setting.
     * @param setting The setting name to be assigned.
     * @param options The options for the assignment process.
     * @returns A function that can be called in a compoenent to assign the setting.
     *
     * @example
     * const assignMySetting = useAssignSetting("MY_SETTING");
     * useEffect(() => {
     *  assignMySetting();
     * }, [assignMySetting]);
     * @example
     * const assignMySetting = useAssignSetting("MY_SETTING");
     * useEffect(() => {
     *  assignMySetting({ submitImmediately: true });
     * }, [assignMySetting]);
     */ function useAssignSetting(settingOrKey) {
        var context = useContext(SettingsContext);
        if (!context) {
            throw new Error("useAssignSetting must be used within a SettingsContextProvider");
        }
        var assignmentAccumulator = context.assignmentAccumulator, assignmentCache = context.assignmentCache;
        return useCallback(function(options) {
            return sharedAssignSetting(settingOrKey, options, assignmentCache.current, assignmentAccumulator.current, throttledBatchAssignSetting);
        }, [
            assignmentAccumulator,
            assignmentCache,
            settingOrKey
        ]);
    };
    var SettingsContext = TopLevelSettingsContext;
    var SettingsContextProvider = function(param) {
        var settings = param.settings, children = param.children;
        var assignmentCache = useRefFrom(function() {
            return new Set();
        });
        var assignmentAccumulator = useRefFrom(function() {
            return new Map();
        });
        useEffect(function() {
            return function() {
                if ("flush" in throttledBatchAssignSetting) {
                    throttledBatchAssignSetting.flush();
                }
            };
        }, []);
        var contextValue = useMemo(function() {
            return {
                settings: settings,
                assignmentCache: assignmentCache,
                assignmentAccumulator: assignmentAccumulator
            };
        }, [
            settings,
            assignmentCache,
            assignmentAccumulator
        ]);
        return /*#__PURE__*/ _jsx(SettingsContext.Provider, {
            value: contextValue,
            children: children
        });
    };
    return {
        useSetting: useSetting,
        useAssignSetting: useAssignSetting,
        SettingsContextProvider: SettingsContextProvider
    };
};
export var throttledBatchAssignSetting = throttleIfPossible(unthrottledBatchAssignSetting, BATCH_SETTING_THROTTLE_MS, {
    leading: false
});
